'use client';

import { useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

interface TimelinePhase {
  id: string;
  day: string;
  title: string;
  description: string;
  deliverables: string[];
  milestones: string[];
}

const phases: TimelinePhase[] = [
  {
    id: 'discover',
    day: 'Days 1–5',
    title: 'Discover',
    description: 'Deep dive into your vision, target audience, and core features',
    deliverables: [
      'Product requirements document',
      'User personas and journey maps',
      'Feature prioritization matrix',
      'Technical architecture blueprint',
    ],
    milestones: [
      'Kickoff call with stakeholders',
      'Competitive analysis completed',
      'MVP scope finalized',
      'Development roadmap approved',
    ],
  },
  {
    id: 'design',
    day: 'Days 6–13',
    title: 'Design',
    description: 'Create intuitive user experiences and pixel-perfect interfaces',
    deliverables: [
      'Wireframes for all key screens',
      'High-fidelity UI mockups',
      'Interactive prototype',
      'Design system and style guide',
    ],
    milestones: [
      'User flow validation',
      'Design review and feedback',
      'Prototype testing',
      'Final design approval',
    ],
  },
  {
    id: 'build',
    day: 'Days 14–22',
    title: 'Build',
    description: 'Transform designs into fully functional, production-ready code',
    deliverables: [
      'Frontend development completed',
      'Backend API integration',
      'Database setup and optimization',
      'Quality assurance testing',
    ],
    milestones: [
      'Core features implemented',
      'Integration testing passed',
      'Performance optimization',
      'Security audit completed',
    ],
  },
  {
    id: 'launch',
    day: 'Days 23–28',
    title: 'Launch',
    description: 'Deploy your MVP and prepare for real user feedback',
    deliverables: [
      'Production deployment',
      'Analytics setup',
      'User documentation',
      'Launch support package',
    ],
    milestones: [
      'Final testing and bug fixes',
      'Production environment ready',
      'MVP launched successfully',
      'Post-launch monitoring active',
    ],
  },
];

const ProcessTimeline = () => {
  const [expandedPhase, setExpandedPhase] = useState<string>('discover');
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start center', 'end center'],
  });

  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
    <section id="process" className="section-pad section-rule relative z-10 bg-background">
      <div className="mx-auto max-w-7xl">
        <motion.div
          className="mb-8 max-w-2xl sm:mb-12 lg:mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <p className="mb-3 font-sans text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            How we ship
          </p>
          <h2 className="mb-3 font-display text-2xl font-bold tracking-tight text-foreground sm:mb-4 sm:text-4xl lg:text-5xl">
            Our 28-day process
          </h2>
          <p className="text-sm leading-relaxed text-text-secondary sm:text-lg">
            A proven framework that transforms your idea into a market-ready MVP.
          </p>
        </motion.div>

        <div className="relative" ref={containerRef}>
          <div className="absolute left-[7px] top-3 hidden h-[calc(100%-24px)] w-px bg-border lg:block" />
          <motion.div
            className="absolute left-[7px] top-3 hidden w-px origin-top bg-primary lg:block"
            style={{ height: lineHeight }}
          />

          <div className="space-y-0">
            {phases.map((phase, index) => {
              const isExpanded = expandedPhase === phase.id;

              return (
                <motion.div
                  key={phase.id}
                  className="relative"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <div className="absolute left-0 top-6 hidden h-3.5 w-3.5 rounded-full border-2 border-primary bg-background lg:block" />

                  <div className="lg:ml-12">
                    <button
                      onClick={() => setExpandedPhase(isExpanded ? '' : phase.id)}
                      className="flex w-full items-start justify-between gap-4 border-t border-border py-6 text-left transition-colors hover:bg-muted/40"
                    >
                      <div className="flex-1">
                        <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span className="font-sans text-xs font-semibold uppercase tracking-[0.15em] text-primary">
                            {phase.day}
                          </span>
                          <span className="font-display text-xl font-semibold text-foreground sm:text-2xl">
                            {phase.title}
                          </span>
                        </div>
                        <p className="text-sm text-text-secondary sm:text-base">{phase.description}</p>
                      </div>
                      <Icon
                        name="ChevronDownIcon"
                        size={20}
                        className={`mt-1 shrink-0 text-text-secondary transition-transform duration-250 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-1 gap-8 pb-8 lg:grid-cols-2">
                            <div>
                              <h4 className="mb-3 font-sans text-xs font-semibold uppercase tracking-[0.15em] text-text-secondary">
                                Deliverables
                              </h4>
                              <ul className="space-y-2">
                                {phase.deliverables.map((item) => (
                                  <li
                                    key={item}
                                    className="flex items-start gap-2 text-sm text-foreground sm:text-base"
                                  >
                                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h4 className="mb-3 font-sans text-xs font-semibold uppercase tracking-[0.15em] text-text-secondary">
                                Key milestones
                              </h4>
                              <ul className="space-y-2">
                                {phase.milestones.map((item) => (
                                  <li
                                    key={item}
                                    className="flex items-start gap-2 text-sm text-foreground sm:text-base"
                                  >
                                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
